import { useEffect, useRef, useState } from "react";
import { Package, ClipboardMinus, Loader2, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, newKey, qty, todayJkt } from "@/lib/api";
import { Field, Loading, PageHeader, QtyInput, SectionTitle, StatusBadge } from "@/components/common";
import { History } from "@/components/History";

function UsageDialog({ open, onClose, stock, onSaved }) {
  const [f, setF] = useState({ material: "kedelai", qty: "", date: todayJkt(), note: "" });
  const [busy, setBusy] = useState(false);
  const key = useRef(newKey());
  const s = stock.find((x) => x.key === f.material);
  const over = s && Number(f.qty) > s.qty;
  const save = async () => {
    if (!(Number(f.qty) > 0)) { toast.error("Isi jumlah pemakaian."); return; }
    if (over) { toast.error(`Stok ${s.name} tidak cukup. Sisa stok: ${qty(s.qty)} ${s.unit}.`); return; }
    setBusy(true);
    try { await api.post("/usage", { ...f, qty: Number(f.qty), client_key: key.current }); toast.success(`Pemakaian ${s.name} ${f.qty} ${s.unit} tercatat.`); key.current = newKey(); setF({ ...f, qty: "", note: "" }); onSaved(); onClose(); }
    catch (e) { toast.error(errMsg(e)); if (e?.response?.status === 409) key.current = newKey(); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-[var(--jt-ivory)]" data-testid="usage-dialog">
        <DialogHeader><DialogTitle>Catat Pemakaian</DialogTitle><DialogDescription>Stok berkurang. Tidak ada pengeluaran uang baru.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Field label="Bahan"><select className="jt-input" value={f.material} onChange={(e) => setF({ ...f, material: e.target.value })} data-testid="usage-material-select">{stock.map((m) => <option key={m.key} value={m.key}>{m.name}</option>)}</select></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Jumlah"><QtyInput value={f.qty} onChange={(v) => setF({ ...f, qty: v })} testid="usage-qty-input" /></Field>
            <Field label="Satuan"><input className="jt-input" value={s?.unit || ""} disabled data-testid="usage-unit" /></Field>
          </div>
          <p className={`text-xs ${over ? "text-red-700 font-bold" : "text-muted-foreground"}`} data-testid="usage-stock-hint">Sisa stok: {qty(s?.qty)} {s?.unit}{over ? " — jumlah melebihi stok yang tersedia." : ""}</p>
          <Field label="Tanggal"><input type="date" className="jt-input" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="usage-date-input" /></Field>
          <Field label="Keterangan"><input className="jt-input" placeholder="Opsional, contoh: produksi pagi" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="usage-note-input" /></Field>
          <button className="jt-btn jt-btn-primary w-full" disabled={busy || over} onClick={save} data-testid="usage-save-button">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Simpan Pemakaian</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MinDialog({ item, onClose, onSaved }) {
  const [v, setV] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (item) setV(String(item.min_stock)); }, [item]);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/materials/${item.key}`, { min_stock: Number(v) || 0 }); toast.success("Batas minimum tersimpan."); onSaved(); onClose(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm bg-[var(--jt-ivory)]" data-testid="min-stock-dialog">
        <DialogHeader><DialogTitle>Batas Minimum {item?.name}</DialogTitle><DialogDescription>Status menjadi “Mulai Menipis” jika stok di bawah batas ini.</DialogDescription></DialogHeader>
        <Field label={`Batas minimum (${item?.unit})`}><QtyInput value={v} onChange={setV} testid="min-stock-input" /></Field>
        <button className="jt-btn jt-btn-primary w-full" disabled={busy} onClick={save} data-testid="min-stock-save-button">Simpan</button>
      </DialogContent>
    </Dialog>
  );
}

export default function Stok() {
  const { version, bump } = useAuth();
  const [stock, setStock] = useState(null);
  const [usage, setUsage] = useState(false);
  const [minItem, setMinItem] = useState(null);
  const [reload, setReload] = useState(0);
  useEffect(() => { api.get("/stock").then(({ data }) => setStock(data.stock)).catch((e) => toast.error(errMsg(e))); }, [version, reload]);
  const changed = () => { setReload((x) => x + 1); bump(); };
  return (
    <div data-testid="stok-page">
      <PageHeader icon={Package} color="#1f7a46" title="Stok Bahan Baku" subtitle="Stok bertambah dari pembelian & kasbon pabrik, berkurang dari pemakaian.">
        <button className="jt-btn jt-btn-primary" onClick={() => setUsage(true)} disabled={!stock} data-testid="open-usage-button"><ClipboardMinus className="w-5 h-5" /> Catat Pemakaian</button>
      </PageHeader>
      {!stock ? <Loading /> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 mb-6">
          {stock.map((s) => (
            <div key={s.key} className="jt-card p-5" data-testid={`stock-card-${s.key}`}>
              <div className="flex items-center justify-between gap-2"><span className="font-extrabold text-[var(--jt-emerald)]">{s.name}</span><StatusBadge status={s.status} testid={`stock-status-${s.key}`} /></div>
              <div className="mt-3 text-2xl font-extrabold tabular-nums" data-testid={`stock-qty-${s.key}`}>{qty(s.qty)} <span className="text-base font-semibold text-muted-foreground">{s.unit}</span></div>
              <button className="mt-3 text-xs font-semibold text-muted-foreground hover:text-foreground flex items-center gap-1" onClick={() => setMinItem(s)} data-testid={`stock-min-button-${s.key}`}>
                <SlidersHorizontal className="w-3.5 h-3.5" /> Batas minimum: {qty(s.min_stock)} {s.unit}
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="jt-card p-5 sm:p-6">
        <SectionTitle>Riwayat Stok</SectionTitle>
        <Tabs defaultValue="in">
          <TabsList className="mb-3"><TabsTrigger value="in" data-testid="stock-tab-in">Riwayat Bahan Masuk</TabsTrigger><TabsTrigger value="out" data-testid="stock-tab-out">Riwayat Pemakaian</TabsTrigger></TabsList>
          <TabsContent value="in"><History kinds="expense,sup_credit,opening_stock" material="any" reloadKey={reload} onChanged={changed} testid="stock-in-history" /></TabsContent>
          <TabsContent value="out"><History kinds="usage" reloadKey={reload} onChanged={changed} testid="stock-usage-history" /></TabsContent>
        </Tabs>
      </div>
      {stock && <UsageDialog open={usage} onClose={() => setUsage(false)} stock={stock} onSaved={changed} />}
      <MinDialog item={minItem} onClose={() => setMinItem(null)} onSaved={changed} />
    </div>
  );
}
